import { useApolloClient, useQuery } from "@apollo/client"
import { gql } from "~/__generated__"
import { TagEdge } from "~/__generated__/graphql"
import { usePublicAgencyId } from "~/contexts/PageVisibilityContext"

export type OptionType = {
  label: string
  value: string
}

const generateOptions = (tagEdges: Omit<TagEdge, "cursor">[]) => {
  return tagEdges.map((edge) => ({
    label: edge.node.name,
    value: edge.node.id,
  }))
}

// On a public roster page the server lists only that agency's public tags,
// and requires the agency when signed out (ZAN-922).
export const useTags = ({ selectedTagIds }: { selectedTagIds: string[] }) => {
  const client = useApolloClient()
  const publicAgencyId = usePublicAgencyId()
  const { data, previousData } = useQuery(TAGS_QUERY_DOCUMENT, {
    variables: { publicAgencyId, tagIdIn: selectedTagIds },
  })

  const tagOptions = generateOptions(
    data?.tags.edges || previousData?.tags.edges || []
  )

  // loadOptions is meant for sourcing AsyncSelect using the property of the same name
  const loadOptions = (
    inputValue: string,
    callback: (options: OptionType[]) => void
  ) => {
    client
      .query({
        query: TAGS_QUERY_DOCUMENT,
        variables: { publicAgencyId, nameContains: inputValue },
      })
      .then(({ data }) => {
        const options = generateOptions(data.tags.edges)
        callback(options)
      })
  }

  return {
    tagOptions,
    loadOptions,
  }
}

export const TAGS_QUERY_DOCUMENT = gql(`
  query Tags($publicAgencyId: ID, $nameContains: String, $tagIdIn: [ID!]) {
    tags(
      publicAgencyId: $publicAgencyId
      nameContains: $nameContains
      tagIdIn: $tagIdIn
    ) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)
